import { useEffect, useState } from 'react';

const KEYBOARD_THRESHOLD = 150;

function setKeyboardCSSVars(height: number, isOpen: boolean) {
  document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
  document.documentElement.style.setProperty('--is-keyboard-open', isOpen ? '1' : '0');
}

function clearKeyboardCSSVars() {
  setKeyboardCSSVars(0, false);
}

export function useKeyboardState() {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    if ('virtualKeyboard' in navigator) {
      const keyboard = (navigator as Navigator & {
        virtualKeyboard: {
          overlaysContent: boolean;
          boundingRect: { height: number };
          addEventListener: (event: 'geometrychange', cb: () => void) => void;
          removeEventListener: (event: 'geometrychange', cb: () => void) => void;
        };
      }).virtualKeyboard;

      keyboard.overlaysContent = true;

      const handleGeometryChange = () => {
        const keyboardHeight = keyboard.boundingRect.height;
        const isOpen = keyboardHeight > 0;
        setIsKeyboardOpen(isOpen);
        setKeyboardCSSVars(keyboardHeight, isOpen);
      };

      keyboard.addEventListener('geometrychange', handleGeometryChange);
      return () => {
        keyboard.removeEventListener('geometrychange', handleGeometryChange);
        clearKeyboardCSSVars();
      };
    }

    const handleResize = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const keyboardHeight = Math.max(window.innerHeight - viewportHeight, 0);
      const isOpen = keyboardHeight > KEYBOARD_THRESHOLD;
      setIsKeyboardOpen(isOpen);
      setKeyboardCSSVars(keyboardHeight, isOpen);
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('scroll', handleResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
      clearKeyboardCSSVars();
    };
  }, []);

  return isKeyboardOpen;
}
